/* eslint-disable no-console */
import { actualizarCotizacion } from "./cotizacionesGuardadas";
import { saveOportunidad } from "./saveOportunidad";
import { client } from "./supabase";
import { updateCliente } from "./updateCliente";

/**
 * Mapea una cotización externa a un registro de sales (oportunidad interna).
 * Recibe la cotización guardada + datos del formulario de formalización.
 */
export function buildSalesFromCotizacion(cotizacion, formData) {
  // Union de todos los IDs de edificios, deduplicados
  const allEdificioIds = [
    ...(cotizacion.edificios_ids_ascensores || "").split(",").filter(Boolean),
    ...(cotizacion.edificios_ids_totems || "").split(",").filter(Boolean),
    ...(cotizacion.edificios_ids_espera || "").split(",").filter(Boolean),
    ...(cotizacion.edificios_ids_cowork || "").split(",").filter(Boolean),
  ];
  const edificiosUnicos = [...new Set(allEdificioIds)];

  // Determinar importe: si hay descuento manual, usar final_amount
  const hasDiscount = (cotizacion.manual_discount_amount || 0) > 0;
  const importe = hasDiscount
    ? Math.floor(cotizacion.final_amount || cotizacion.inversion_final_total || 0)
    : Math.floor(cotizacion.inversion_final_total || 0);

  // Formatear mes_cierre y mes_factura como "MM-YYYY"
  const mesCierre = `${formData.mes_cierre}-${formData.ano_cierre}`;
  const mesFactura = `${formData.mes_factura}-${formData.ano_factura}`;

  return {
    code: cotizacion.codigo_generado,
    cliente: formData.cliente_nombre || cotizacion.cliente_nombre || "Sin cliente",
    client_id: formData.cliente_id || cotizacion.cliente_id || null,
    campana: formData.campana || null,
    canal: formData.canal || null,
    pais: cotizacion.pais,
    tipo: cotizacion.is_programmatic_selected ? "p" : "d",
    moneda_dolar: cotizacion.moneda_dolar || false,
    importe,
    estatus: formData.estatus,
    ejecutivo: null, // se resuelve abajo desde el cliente
    intermediario: formData.intermediario_nombre || null,
    mes_cierre: mesCierre,
    mes_factura: mesFactura,
    ano: parseInt(formData.ano_factura),
    // Edificios e inventario
    pantallas_ascensores: cotizacion.edificios_ids_ascensores || "",
    pantallas_circulacion: cotizacion.edificios_ids_totems || "",
    pantallas_espera: cotizacion.edificios_ids_espera || "",
    pantallas_cowork: cotizacion.edificios_ids_cowork || "",
    edificiosparticipantes: edificiosUnicos.join(","),
    edificiostotales: cotizacion.edificios_unicos_total || 0,
    pantallastotales: (cotizacion.ascensores_seleccionados || 0) + (cotizacion.totems_seleccionados || 0),
    // Parámetros de campaña
    formatopauta: String(cotizacion.frecuencia || 0),
    periodopauta: cotizacion.plazo || 0,
    duracion: cotizacion.duracion || 0,
    horas_pauta: cotizacion.horas_final || cotizacion.horas || 0,
    intervalos: JSON.stringify(cotizacion.intervalos || []),
    options: JSON.stringify(cotizacion.options || []),
    // Descuento
    has_manual_discount: hasDiscount,
    manual_discount_amount: hasDiscount ? Math.floor(cotizacion.manual_discount_amount) : null,
    original_amount: hasDiscount ? Math.floor(cotizacion.inversion_final_total || 0) : null,
    // Métricas del cotizador
    audiencia_total: cotizacion.audiencia_seleccionada_total || null,
    impactos_totales: cotizacion.impactos_totales || null,
    salidas_totales: cotizacion.salidas_totales || null,
    tarifa_fria_total: cotizacion.tarifa_fria_total || null,
    descuento_pct: cotizacion.descuento_total_final || null,
    cpm_total: cotizacion.cpm_total_final || null,
    cpm_exhibicion:
      cotizacion.salidas_totales > 0
        ? ((cotizacion.inversion_final_total || 0) / cotizacion.salidas_totales) * 1000
        : null,
    // Defaults
    version: 1,
    eliminada: false,
    facturafecha: null,
    plazocobro: null,
    facturafechacobro: null,
    facturanumero: null,
    comision_agencia: null,
  };
}

/**
 * Formaliza una cotización externa: crea oportunidad en sales,
 * actualiza CRM del cliente, y marca la cotización como ejecutada.
 *
 * @param {Object} cotizacion - Registro completo de la tabla cotizaciones
 * @param {Object} formData - Datos del formulario de formalización:
 *   { cliente_id, cliente_nombre, campana, canal, estatus,
 *     mes_cierre, ano_cierre, mes_factura, ano_factura,
 *     intermediario_nombre, tipo_cotizacion ("agencia"|"cliente") }
 * @returns {Object} El registro de sales creado
 */
export async function formalizarCotizacion(cotizacion, formData) {
  // 1. Crear oportunidad en sales
  const salesRecord = buildSalesFromCotizacion(cotizacion, formData);

  // Resolver ejecutivo desde el cliente (dueño de la cuenta)
  if (formData.cliente_id) {
    const { data: cli } = await client.from("clientes").select("ejecutivo").eq("id", formData.cliente_id).single();
    if (cli?.ejecutivo) salesRecord.ejecutivo = cli.ejecutivo;
  }

  const result = await saveOportunidad(salesRecord);

  // 2. Actualizar CRM del cliente (mismo flujo que handleLinkOportunidad)
  if (formData.cliente_id) {
    const today = new Date();
    const nextContactDate = new Date(today);
    nextContactDate.setDate(today.getDate() + 7);
    const formatDate = (date) => date.toISOString().split("T")[0];

    const updatedClienteData = { id: formData.cliente_id };
    const tipoCot = formData.tipo_cotizacion || "agencia";

    if (tipoCot === "cliente") {
      updatedClienteData.ultimo_contacto_fecha_cliente = formatDate(today);
      updatedClienteData.ultimo_contacto_accion_cliente = "QUOTE";
      updatedClienteData.proximo_contacto_fecha_cliente = formatDate(nextContactDate);
      updatedClienteData.proximo_contacto_accion_cliente = "FOLLOW UP";
    } else {
      updatedClienteData.ultimo_contacto_fecha_agencia = formatDate(today);
      updatedClienteData.ultimo_contacto_accion_agencia = "QUOTE";
      updatedClienteData.proximo_contacto_fecha_agencia = formatDate(nextContactDate);
      updatedClienteData.proximo_contacto_accion_agencia = "FOLLOW UP";
    }

    try {
      await updateCliente(updatedClienteData);
    } catch (error) {
      // No fallar si la actualización de CRM falla, la oportunidad ya se creó
      console.error("Error actualizando CRM del cliente:", error);
    }
  }

  // 3. Marcar cotización como ejecutada
  await actualizarCotizacion(cotizacion.id, { estado: "ejecutada" });

  return result;
}
